import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpService } from '../../http.service';
import { buildPeriodOptions, granularityOf, PeriodOption } from './period-resolver';

@Component({
  selector: 'app-agent-account-detail',
  templateUrl: './agent-account-detail.component.html',
  styleUrls: ['./agent-account-detail.component.scss'],
})
export class AgentAccountDetailComponent implements OnInit {
  public agent: any = null;
  public periodOptions: PeriodOption[] = [];
  public selectedPeriodKey: string = 'current_month';
  public period: PeriodOption = { key: 'current_month', label: 'Current Month', from_date: '', to_date: '' };
  public dailyBreakdown: any[] = [];
  public periodCommission = 0;
  public loading = false;
  public isRedShareMode = false;
  private agent_code = '';

  gameBreakdown(row: any): any[] {
    const breakdown = row?.breakdown;
    if (!breakdown || !Array.isArray(breakdown.game_breakdown)) return [];
    return breakdown.game_breakdown;
  }

  percent(value: any): string {
    return `${(Number(value || 0) * 100).toFixed(0)}%`;
  }

  // fixed total rolling rate (1% of loss), shared by all levels
  private static readonly ROLLING_RATE = 0.01;

  calcText(item: any): string {
    const profit = Number(item?.profit || 0).toFixed(3);
    const gameRate = Number(item?.game_rate || 0);
    const poolRate = Number(item?.pool_rate || item?.poolRate || 0.70);
    const poolPct = Math.round(poolRate * 100 * 100) / 100;
    const rolling = AgentAccountDetailComponent.ROLLING_RATE;
    // client-facing spelling: Lose × GameRate × 1% × Rate × PlayerShare.
    // GameRate is the automated-rule rate (per game, general fallback — today 0.005 → 50%)
    // and Rate is the stored level rolling rate: L1 100% / L2 30% / L3 20%.
    if (item?.tier_rate != null) {
      const tierRate = Number(item.tier_rate);
      const dispGameRate = gameRate / rolling;
      const gameRatePct = Math.round(dispGameRate * 100 * 100) / 100;
      const rollingPct = Math.round(rolling * 100 * 100) / 100;
      const ratePct = Math.round(tierRate * 100 * 100) / 100;
      return `${profit} × ${gameRatePct}% × ${rollingPct}% × ${ratePct}% × ${poolPct}%`;
    }
    // older 3-factor rows have no tier_rate — there game_rate is already the effective rate
    const ratePct = Math.round(gameRate * 10000 * 100) / 100;
    return `${profit} × ${ratePct}% × ${poolPct}%`;
  }

  constructor(private http: HttpService, private route: ActivatedRoute) {}

  async ngOnInit() {
    this.agent_code = this.route.snapshot.paramMap.get('agent_code') || '';
    await this.init();
  }

  async init() {
    const s: any = await this.http.post('/admin/setting/get');
    const rs = s?.agent_red_share_mode;
    this.isRedShareMode = rs === true || rs === 1 || rs === '1' || rs === 'true';
    const setting = s?.agent_account_period || 'current_month';

    this.periodOptions = buildPeriodOptions(granularityOf(setting), 12, new Date());
    this.selectedPeriodKey = setting;
    const preselect = this.periodOptions.find(o => o.key === setting) || this.periodOptions[0];
    this.period = preselect;
    this.selectedPeriodKey = this.period.key;

    await this.load();
  }

  async onPeriodChange(key: string) {
    const opt = this.periodOptions.find(o => o.key === key);
    if (!opt) return;
    this.period = opt;
    this.selectedPeriodKey = key;
    await this.load();
  }

  async load() {
    this.loading = true;
    try {
      const res: any = await this.http.post('/admin/agent-account/reports', {
        agent_code: this.agent_code,
        from_date: this.period.from_date,
        to_date: this.period.to_date,
      });
      if (res?.status) {
        this.agent = res.agent;
        const daily: any[] = Array.isArray(res.report) ? res.report : (res.report?.daily || []);
        this.dailyBreakdown = [...daily].sort(
          (a: any, b: any) => String(b.date).localeCompare(String(a.date))
        );
        this.periodCommission = this.dailyBreakdown.reduce(
          (sum: number, r: any) => sum + Number(r.commission || 0), 0
        );
      } else {
        this.dailyBreakdown = [];
        this.periodCommission = 0;
      }
    } finally {
      this.loading = false;
    }
  }
}
